import { VaultMediaType } from '@api'
import { attach, createEffect, createStore, sample } from 'effector'

import { $creatorId } from '@/models/creators.ts'
import { $vaultMediasMap, loadMultipleMediasFx } from '@/models/vault/medias'
import type { SentMediaMessage } from '@/modules/chatting/types.ts'
import type { TimelineData } from '@/modules/chatting/ui/chat-window/fan-info/ppv-stats/ppv-timeline/types'
import type { StreamableVaultMedia } from '@/modules/vault/types.ts'
import { getVaultImageUrl } from '@/utils/url.ts'

const audioImage =
  'https://cdn.pixabay.com/photo/2017/02/27/19/34/microphone-2104091_1280.png'

export const getTimelineDataFx = attach({
  name: 'get timeline data',
  source: {
    vaultMediasMap: $vaultMediasMap,
    creatorId: $creatorId,
  },
  mapParams: (
    ppvTimelineData: SentMediaMessage[],
    { vaultMediasMap, creatorId },
  ) => ({
    ppvTimelineData,
    vaultMediasMap,
    creatorId: creatorId!,
  }),
  effect: createEffect(
    async ({
      ppvTimelineData,
      vaultMediasMap,
      creatorId,
    }: {
      ppvTimelineData: SentMediaMessage[]
      vaultMediasMap: Record<number, StreamableVaultMedia>
      creatorId: number
    }) => {
      return Promise.all(
        ppvTimelineData
          .filter((item) => item.price > 0 && item.media.length > 0)
          .flatMap((item) =>
            item.media.reduce(
              (result, media) => {
                const originalMedia = vaultMediasMap?.[media.id]

                if (
                  originalMedia?.type === VaultMediaType.AUDIO &&
                  !result.preview
                ) {
                  // if no preview at all and only audio - set preview for audio
                  result.preview = audioImage
                } else if (
                  (!result.preview || result.preview === audioImage) &&
                  [VaultMediaType.PHOTO, VaultMediaType.VIDEO].includes(
                    // @ts-ignore
                    originalMedia?.type,
                  )
                ) {
                  result.preview = originalMedia?.files.thumb?.url || ''
                }

                if (originalMedia?.type === VaultMediaType.PHOTO) {
                  result.photoCount++
                } else if (originalMedia?.type === VaultMediaType.VIDEO) {
                  result.videoCount++
                } else if (originalMedia?.type === VaultMediaType.AUDIO) {
                  result.audioCount++
                }

                return result
              },
              {
                createdAt: item.createdAt,
                price: item.price,
                isOpened: item.isOpened,
                photoCount: 0,
                audioCount: 0,
                videoCount: 0,
                text: item.text,
                preview: '',
                messageId: item.id,
              },
            ),
          )
          .map(async (item) => ({
            ...item,
            preview:
              item.preview === audioImage
                ? item.preview
                : await getVaultImageUrl(item.preview, creatorId!),
          })),
      )
    },
  ),
})

export const $timelineData = createStore<TimelineData[]>([]).on(
  getTimelineDataFx.doneData,
  (_, data) => data,
)

sample({
  clock: getTimelineDataFx,
  source: {
    vaultMediasMap: $vaultMediasMap,
  },
  fn: ({ vaultMediasMap }, ppvTimelineData): number[] => {
    const ids: number[] = []
    const filteredTimelineData = ppvTimelineData.filter(
      (item) => item.price > 0 && item.media.length > 0,
    )

    filteredTimelineData.forEach((item) => {
      item.media.forEach((media) => {
        if (
          !media.files?.thumb?.url &&
          !ids.includes(media.id) &&
          !vaultMediasMap[media.id]
        ) {
          ids.push(media.id)
        }
      })
    })

    return ids
  },
  target: loadMultipleMediasFx,
})
